/**
 * TanStack Query hooks for punch items.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FloorplanPin,
  PunchItem,
  PunchItemCreateInput,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

type PunchItemListResponse = { data: PunchItem[] };

function listKey(taskId: string) {
  return ['punch-items', 'list', taskId] as const;
}

export function usePunchItems(taskId: string | undefined) {
  return useQuery<PunchItemListResponse>({
    queryKey: listKey(taskId ?? ''),
    enabled: !!taskId,
    queryFn: () =>
      apiCall<PunchItemListResponse>(`/tasks/${taskId}/punch-items`),
  });
}

export function useCreatePunchItem(taskId: string) {
  const qc = useQueryClient();
  return useMutation<PunchItem, Error, Omit<PunchItemCreateInput, 'task_id'>>({
    mutationFn: (body) =>
      apiCall<PunchItem>(`/tasks/${taskId}/punch-items`, {
        method: 'POST',
        body,
      }),
    onSuccess: (created) => {
      // The item may have been rerouted to the project's punch task
      // (VCS-101) — invalidate every punch list, not just the source task's,
      // and the destination task's detail. The rollup can also regress the
      // project status (pending_closeout → punch), so projects refresh too.
      qc.invalidateQueries({ queryKey: ['punch-items'] });
      qc.invalidateQueries({ queryKey: listKey(created.task_id) });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['crew-task-detail'] });
    },
  });
}

export function useUpdatePunchItem(taskId: string) {
  const qc = useQueryClient();
  return useMutation<
    PunchItem,
    Error,
    {
      id: string;
      body: Partial<{
        title: string;
        description: string | null;
        status: PunchItem['status'];
        blocks_recap: boolean;
        zone_tag: string | null;
        floorplan_pin: FloorplanPin | null;
      }>;
    }
  >({
    mutationFn: ({ id, body }) =>
      apiCall<PunchItem>(`/punch-items/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(taskId) });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export function useClosePunchItem(taskId: string) {
  const qc = useQueryClient();
  return useMutation<PunchItem, Error, { id: string; note?: string }>({
    mutationFn: ({ id, note }) =>
      apiCall<PunchItem>(`/punch-items/${id}/close`, {
        method: 'POST',
        body: note ? { note } : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(taskId) });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export function useDeletePunchItem(taskId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiCall<void>(`/punch-items/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(taskId) });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
